import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as Popover from '@radix-ui/react-popover';
import getErrorInfo from './lib/getErrorInfo.jsx';
import LogOperationRow from './logOperationLogsPopover/LogOperationRow.jsx';
import Button from '@/components/Button.jsx';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { query } from '@/redux/selectors/querySelectors.js';
import getTimeMachineEntry from '@/screens/workflows/redux/actions/getTimeMachineEntry.js';
import setQuery from '@/lib/setQuery.js';
import selectors from '@/redux/selectors/selectors.js';

const LogOperationLogsPopover = () => {
  const [isOpen, setIsOpen] = useState(false);
  const activeLogId = useArgSelector(query, 'logId');
  const operationLogs = useSelector(selectors.workflowOperationLogsByLogId);

  const handleOpen = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsOpen(true);
  };

  const handleView = (log) => {
    const logStepId = log?.stepId;
    const { index } =
      getTimeMachineEntry({
        logId: activeLogId,
        operationLog: log
      }) || {};

    if (index) {
      setQuery({ logStepId, time: index, scrollTo: logStepId });
    }
    setIsOpen(false);
  };

  const { sortedErrorLogs, sortedWarnLogs, sortedInfoLogs, icon, label, status } = useMemo(() => {
    const filteredOperationLogs = operationLogs?.filter((n) => ['error', 'warn', 'info']?.includes(n?.actionStatus));
    const sortedOperationLogs = filteredOperationLogs?.sort((a, b) => new Date(a?.createdOn) - new Date(b?.createdOn));

    const errorInfo = getErrorInfo(sortedOperationLogs);

    return {
      sortedErrorLogs: sortedOperationLogs?.filter((n) => n?.actionStatus === 'error'),
      sortedWarnLogs: sortedOperationLogs?.filter((n) => n?.actionStatus === 'warn'),
      sortedInfoLogs: sortedOperationLogs?.filter((n) => n?.actionStatus === 'info'),
      ...errorInfo
    };
  }, [operationLogs]);

  if (!operationLogs?.length || !label) {
    return null;
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <div onClick={handleOpen}>
          <Button className={`ghost status-${status}`} icon={icon} onClick={handleOpen}>
            {label}
          </Button>
        </div>
      </Popover.Trigger>
      <Popover.Portal container={document.querySelector('.log-steps-wrapper')}>
        <Popover.Content className="log-operation-logs-popover">
          {sortedErrorLogs?.length > 0 && (
            <>
              <p className="title">Errors</p>
              <div className="rows">
                {sortedErrorLogs?.map((n, idx) => (
                  <LogOperationRow key={`${n?.id}-${idx}`} item={n} onView={handleView} />
                ))}
              </div>
            </>
          )}
          {sortedWarnLogs?.length > 0 && (
            <>
              <p className="title warning">Warnings</p>
              <div className="rows">
                {sortedWarnLogs?.map((n, idx) => (
                  <LogOperationRow key={`${n?.id}-${idx}`} item={n} onView={handleView} />
                ))}
              </div>
            </>
          )}
          {sortedInfoLogs?.length > 0 && (
            <>
              <p className="title info">Info</p>
              <div className="rows">
                {sortedInfoLogs?.map((n, idx) => (
                  <LogOperationRow key={`${n?.id}-${idx}`} item={n} onView={handleView} />
                ))}
              </div>
            </>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default LogOperationLogsPopover;
